/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import { Link } from "react-router-dom"
import IconButton from "@mui/material/IconButton"
import MenuIcon from "@mui/icons-material/Menu"
import Drawer from "@mui/material/Drawer"
import List from "@mui/material/List"
import ListItem from "@mui/material/ListItem"
import ListItemText from "@mui/material/ListItemText"
import Divider from "@mui/material/Divider"
import { getSettings } from "../../../../services/settings"
import { getImageUrl } from "../../../../services/listings"

const DEFAULT_NAV_LINKS = [
    {
        label: "Buyer Questionnaire",
        url: "https://i8grr76tpqp.typeform.com/to/c7JWrqnv?typeform-source=linktr.ee",
        isExternal: true,
        variant: "text",
    },
    {
        label: "Meet Kaden",
        url: "https://beerhometeam.hifello.com/p/kaden.cowles",
        isExternal: true,
        variant: "text",
    },
    {
        label: "Seller Questionnaire",
        url: "https://i8grr76tpqp.typeform.com/to/BlERdHqS",
        isExternal: true,
        variant: "text",
    },
    {
        label: "Bookings",
        url: "https://krispvacations.guestybookings.com/",
        isExternal: true,
        variant: "text",
    },
    {
        label: "About",
        url: "/page-about",
        isExternal: false,
        variant: "text",
    },
    {
        label: "Contact Us",
        url: "/page-contact",
        isExternal: false,
        variant: "contained",
    },
]

const Topbar = () => {
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [logoSrc, setLogoSrc] = useState("/krisp.webp")
    const [navLinks, setNavLinks] = useState(DEFAULT_NAV_LINKS)

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const settings = await getSettings()
                if (settings.logo) {
                    setLogoSrc(getImageUrl(settings.logo))
                }
                if (settings.navLinks && settings.navLinks.length > 0) {
                    setNavLinks(settings.navLinks)
                }
            } catch (error) {
                // Fall back to defaults
            }
        }
        fetchSettings()
    }, [])

    const toggleDrawer = (open) => (event) => {
        if (
            event.type === "keydown" &&
            (event.key === "Tab" || event.key === "Shift")
        ) {
            return
        }
        setDrawerOpen(open)
    }

    const renderDesktopLink = (link, index) => {
        const isLast = index === navLinks.length - 1
        const commonProps = {
            variant: link.variant || "text",
            size: "large",
            sx: link.variant === "contained" ? {} : { fontWeight: "bold" },
            ...(link.variant === "contained" && { color: "primary" }),
        }

        if (link.isExternal) {
            return (
                <Box key={index} marginX={isLast && link.variant === "contained" ? 0 : 2}>
                    <Button
                        {...commonProps}
                        component="a"
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {link.label}
                    </Button>
                </Box>
            )
        }

        return (
            <Box key={index} marginX={isLast && link.variant === "contained" ? 0 : 2}>
                <Button
                    {...commonProps}
                    component={Link}
                    to={link.url}
                >
                    {link.label}
                </Button>
            </Box>
        )
    }

    const renderDrawerLink = (link, index) => {
        if (link.isExternal) {
            return (
                <ListItem
                    key={index}
                    button
                    component="a"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <ListItemText primary={link.label} />
                </ListItem>
            )
        }

        return (
            <ListItem key={index} button component={Link} to={link.url}>
                <ListItemText primary={link.label} />
            </ListItem>
        )
    }

    return (
        <Box
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
            width={"100%"}
        >
            {/* Logo Section */}
            <Box display={"flex"} alignItems={"center"}>
                <Link to="/" style={{ display: "flex" }}>
                    <img
                        src={logoSrc}
                        alt="Logo"
                        style={{
                            width: "clamp(80px, 14vw, 140px)",
                        }}
                    />
                </Link>
            </Box>

            {/* Links and Hamburger Menu */}
            <Box display="flex" alignItems={"center"}>
                <Box
                    sx={{ display: { xs: "none", lg: "flex" } }}
                    alignItems={"center"}
                >
                    {navLinks.map(renderDesktopLink)}
                </Box>

                {/* Hamburger menu for small screens */}
                <IconButton
                    edge="start"
                    color="primary.main"
                    aria-label="menu"
                    onClick={toggleDrawer(true)}
                    sx={{ display: { xs: "flex", lg: "none" } }}
                >
                    <MenuIcon />
                </IconButton>
            </Box>

            {/* Drawer for small screens */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
            >
                <Box
                    sx={{ width: 250 }}
                    role="presentation"
                    onClick={toggleDrawer(false)}
                    onKeyDown={toggleDrawer(false)}
                >
                    <List>
                        {navLinks.map(renderDrawerLink)}
                    </List>
                    <Divider />
                </Box>
            </Drawer>
        </Box>
    )
}

Topbar.propTypes = {
    themeToggler: PropTypes.func.isRequired,
    themeMode: PropTypes.string.isRequired,
}

export default Topbar
